import {
  ArrowLeftIcon,
  MagnifyingGlassIcon,
  PersonIcon,
} from "@radix-ui/react-icons";
import { useMutation, useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useDebounce } from "@uidotdev/usehooks";
import { useState } from "react";

import { BoundLists } from "@/components/BoundLists";
import { CollaboratorActivity } from "@/components/CollaboratorActivity";
import { FeedAccessErrorPage } from "@/components/FeedAccessErrorPage";
import { Page } from "@/components/Page";
import { Spinner } from "@/components/Spinner";
import { Avatar } from "@/components/ui/avatar";
import { Button, ButtonLink } from "@/components/ui/button";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ViewFeedHeader } from "@/components/ViewFeedHeader";
import {
  actorSearchQueryOptions,
  addCollaborator,
  capabilityCatalogueQueryOptions,
  collaboratorEventsQueryOptions,
  collaboratorsQueryOptions,
  feedQueryOptions,
  getCurrentUserQueryOptions,
  hydratedUsersByDidQueryOptions,
  removeCollaborator,
  updateCollaborator,
  type ActorCandidate,
  type CapabilityInfo,
  type FeedCapability,
  type FeedCollaborator,
} from "@/lib/api";
import type { User } from "@/lib/types";
import { cn } from "@/lib/utils";

/**
 * Who else can work on this feed, and exactly what they can do.
 *
 * TWO THINGS THIS PAGE HAS TO MAKE OBVIOUS, because getting either wrong is how a feed gets
 * changed by someone who should not have been able to:
 *
 *  1. "Add posts naturally" and "add posts to the top" are different powers. One says the
 *     post belonged in the feed; the other says everyone should see it now. They are next to
 *     each other in the list and the copy spells out the difference, because a permission
 *     screen that reads as a single spectrum invites ticking everything.
 *
 *  2. You can only hand over what you hold. The server refuses otherwise, and
 *     `you_may_grant` tells us which boxes to disable — so a non-owner sees WHY a capability
 *     is unavailable rather than being refused after filling the form in.
 *
 * The capability vocabulary is fetched, never hardcoded: the labels and descriptions come
 * from `/api/v1/collab/capabilities` so the server stays the one definition.
 */
export const Route = createFileRoute(
  "/app/_sidebar-layout/feed/$feedId/collaborators",
)({
  component: RouteComponent,
  // A revoked collaborator gets 403 here; say so instead of bouncing them to /login.
  errorComponent: FeedAccessErrorPage,
  loader: async ({ context: { queryClient }, params: { feedId } }) => {
    const id = parseInt(feedId);
    await Promise.all([
      queryClient.ensureQueryData(feedQueryOptions(id)),
      queryClient.ensureQueryData(collaboratorsQueryOptions(id)),
      queryClient.ensureQueryData(capabilityCatalogueQueryOptions()),
    ]);
  },
  head: () => ({ meta: [{ title: "Collaborators | Graze" }] }),
});

function CapabilityPicker({
  catalogue,
  selected,
  grantable,
  onChange,
  idPrefix,
}: {
  catalogue: CapabilityInfo[];
  selected: FeedCapability[];
  grantable: FeedCapability[];
  onChange: (next: FeedCapability[]) => void;
  idPrefix: string;
}) {
  const toggle = (key: FeedCapability) => {
    const has = selected.includes(key);
    let next = has
      ? selected.filter((c) => c !== key)
      : [...selected, key];
    // Mirror the server's implication so the checkboxes cannot show a state the server
    // would silently correct: granting "to the top" always includes "naturally".
    if (!has && key === "add_posts_promoted" && !next.includes("add_posts_organic")) {
      next = [...next, "add_posts_organic"];
    }
    if (has && key === "add_posts_organic") {
      next = next.filter((c) => c !== "add_posts_promoted");
    }
    onChange(next);
  };

  return (
    <fieldset className="space-y-2">
      <legend className="sr-only">Capabilities</legend>
      {catalogue.map((cap) => {
        const allowed = grantable.includes(cap.key);
        const id = `${idPrefix}-${cap.key}`;
        return (
          <div
            key={cap.key}
            className={cn("flex gap-3 rounded border p-3", !allowed && "opacity-60")}
          >
            <input
              id={id}
              type="checkbox"
              className="mt-1 size-4 shrink-0"
              checked={selected.includes(cap.key)}
              disabled={!allowed}
              onChange={() => toggle(cap.key)}
            />
            <label htmlFor={id} className="cursor-pointer">
              <span className="block text-sm font-medium">{cap.label}</span>
              <span className="block text-xs text-gray-600 dark:text-gray-400">
                {cap.description}
              </span>
              {!allowed && (
                <span className="mt-1 block text-xs text-amber-700 dark:text-amber-400">
                  You cannot grant this, because you do not have it yourself.
                </span>
              )}
            </label>
          </div>
        );
      })}
    </fieldset>
  );
}

/** One search result, drawn the way the collaborator list draws a person. */
function ActorRow({ actor }: { actor: ActorCandidate }) {
  const name = actor.display_name || actor.handle || actor.did;

  return (
    <div className="flex min-w-0 items-center gap-3">
      <Avatar
        src={actor.avatar ?? undefined}
        alt={`${name}'s avatar`}
        size="sm"
        fallback={<PersonIcon />}
      />
      <div className="min-w-0">
        <div className="truncate text-sm font-medium">{name}</div>
        {actor.handle && (
          <div className="truncate text-xs text-gray-600 dark:text-gray-400">
            @{actor.handle}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Pick the person, don't type them.
 *
 * 🔑 THE POINT IS CONFIRMATION, NOT CONVENIENCE. A free-text handle box cannot be checked by
 * the person using it: `alice.bsky.social` and `alíce.bsky.social` look alike, and the first
 * sign of a mistake is a stranger editing your feed. Here you see the avatar and name of the
 * account you are about to trust, and what gets submitted is its DID — exact, and not
 * re-resolved on the server where it could land on someone else.
 *
 * Still accepts a pasted handle or DID: that path resolves to the same confirmable card, so
 * someone who already knows the identifier is not forced through a search.
 */
function ActorPicker({
  selected,
  onSelect,
}: {
  selected: ActorCandidate | null;
  onSelect: (actor: ActorCandidate | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState("");
  const debouncedTerm = useDebounce(term, 400);
  const {
    data: actors,
    isFetching,
    isError,
  } = useQuery(actorSearchQueryOptions(debouncedTerm));

  return (
    <div className="space-y-1">
      <span className="text-sm font-medium">Who are you adding?</span>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label="Search for the person to add"
            className="flex w-full items-center justify-between gap-2 rounded-md border px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-900"
          >
            {selected ? (
              <ActorRow actor={selected} />
            ) : (
              <span className="text-sm text-gray-500">
                Search by name or handle…
              </span>
            )}
            <MagnifyingGlassIcon className="shrink-0 text-gray-500" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Name, handle or DID"
              value={term}
              onValueChange={setTerm}
            />
            <CommandList>
              {!debouncedTerm.trim() ? (
                <div className="px-3 py-6 text-center text-sm text-gray-500">
                  Start typing to find someone.
                </div>
              ) : isError ? (
                /* Distinguished from "nobody found" on purpose -- see the endpoint. */
                <div className="px-3 py-6 text-center text-sm text-red-600">
                  Could not search just now. Try again in a moment.
                </div>
              ) : isFetching && !actors ? (
                <div className="flex h-16 items-center justify-center text-gray-500">
                  <Spinner />
                </div>
              ) : !actors || actors.length === 0 ? (
                <div className="px-3 py-6 text-center text-sm text-gray-500">
                  No accounts match “{debouncedTerm}”.
                </div>
              ) : (
                <CommandGroup>
                  {actors.map((actor) => (
                    <CommandItem
                      key={actor.did}
                      value={actor.did}
                      onSelect={() => {
                        onSelect(actor);
                        setOpen(false);
                      }}
                    >
                      <ActorRow actor={actor} />
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {selected && (
        <button
          type="button"
          className="text-xs text-gray-500 underline-offset-2 hover:underline"
          onClick={() => onSelect(null)}
        >
          Choose someone else
        </button>
      )}
    </div>
  );
}

function AddCollaboratorForm({
  feedId,
  catalogue,
  grantable,
  presets,
}: {
  feedId: number;
  catalogue: CapabilityInfo[];
  grantable: FeedCapability[];
  presets: Record<string, FeedCapability[]>;
}) {
  const [actor, setActor] = useState<ActorCandidate | null>(null);
  const [note, setNote] = useState("");
  const [caps, setCaps] = useState<FeedCapability[]>(["add_posts_organic"]);
  const [error, setError] = useState<string | null>(null);

  const add = useMutation({
    mutationFn: () =>
      addCollaborator({
        feedId,
        // The DID, never the handle: a handle can be reassigned between picking and saving.
        actor: actor?.did ?? "",
        capabilities: caps,
        note: note || undefined,
      }),
    onSuccess: () => {
      setActor(null);
      setNote("");
      setCaps(["add_posts_organic"]);
      setError(null);
    },
    onError: (e: unknown) =>
      setError(
        (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ??
          "Could not add that person.",
      ),
  });

  return (
    <div className="max-w-2xl space-y-4 rounded-lg border p-6">
      <h2 className="text-lg font-semibold">Add someone</h2>
      <div className="grid gap-3 sm:grid-cols-2">
        <ActorPicker selected={actor} onSelect={setActor} />
        <label htmlFor="collab-note" className="block space-y-1">
          <span className="text-sm font-medium">
            Note <span className="font-normal text-gray-500">(optional)</span>
          </span>
          <Input
            id="collab-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="What they're here for, e.g. weekend editor"
          />
        </label>
      </div>

      {Object.keys(presets).length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-gray-500">Start from:</span>
          {Object.entries(presets).map(([name, keys]) => (
            <Button
              key={name}
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setCaps(keys.filter((k) => grantable.includes(k)))}
            >
              {name}
            </Button>
          ))}
        </div>
      )}

      <CapabilityPicker
        catalogue={catalogue}
        selected={caps}
        grantable={grantable}
        onChange={setCaps}
        idPrefix="new"
      />

      {error && <p className="text-sm text-red-600">{error}</p>}
      <Button
        onClick={() => add.mutate()}
        disabled={!actor || caps.length === 0 || add.isPending}
      >
        {add.isPending ? "Adding…" : "Add collaborator"}
      </Button>
      {caps.length === 0 && actor && (
        <p className="text-xs text-gray-500">
          Pick at least one capability — adding someone with none would give them nothing.
        </p>
      )}
    </div>
  );
}

/**
 * A person, as a person.
 *
 * The list stores DIDs because that is the only identifier that cannot be taken away and
 * handed to someone else — but `did:plc:ri4c…` tells nobody whether the right colleague was
 * added. So the row shows the avatar, display name and handle, with the DID underneath as
 * the thing you check when two people share a display name.
 *
 * Hydration is best-effort: if the profile fetch has not landed, or the account cannot be
 * resolved at all, the DID stands in rather than the row collapsing.
 */
function CollaboratorIdentity({
  did,
  profile,
  userId,
  isYou,
}: {
  did: string | null;
  profile?: User;
  userId: number;
  isYou: boolean;
}) {
  const fallbackName = did ?? `user ${userId}`;
  const name = profile?.display_name || profile?.handle || fallbackName;

  return (
    <div className="flex min-w-0 gap-3">
      <Avatar
        src={profile?.avatar}
        alt={profile ? `${name}'s avatar` : ""}
        size="sm"
        fallback={<PersonIcon />}
        className="mt-0.5"
      />
      <div className="min-w-0">
        <p className="truncate text-sm font-medium">
          {name}
          {isYou && <span className="ml-2 text-xs text-gray-500">(you)</span>}
        </p>
        {profile?.handle && (
          <p className="truncate text-xs text-gray-600 dark:text-gray-400">
            @{profile.handle}
          </p>
        )}
        {/* Not repeated when it is already standing in as the name. */}
        {did && name !== did && (
          <p className="truncate text-[11px] text-gray-500" title={did}>
            {did}
          </p>
        )}
      </div>
    </div>
  );
}

function CollaboratorRow({
  feedId,
  collaborator,
  profile,
  catalogue,
  grantable,
  canManage,
  isYou,
}: {
  feedId: number;
  collaborator: FeedCollaborator;
  profile?: User;
  catalogue: CapabilityInfo[];
  grantable: FeedCapability[];
  canManage: boolean;
  isYou: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const [caps, setCaps] = useState<FeedCapability[]>(collaborator.capabilities);
  const [error, setError] = useState<string | null>(null);
  const name =
    profile?.display_name || profile?.handle || collaborator.did || "this person";
  const fromList = collaborator.source_list_id !== null;

  const save = useMutation({
    mutationFn: () =>
      updateCollaborator({
        feedId,
        collaboratorId: collaborator.id,
        capabilities: caps,
      }),
    onSuccess: () => {
      setEditing(false);
      setError(null);
    },
    onError: (e: unknown) =>
      setError(
        (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ??
          "Could not save.",
      ),
  });

  const remove = useMutation({
    mutationFn: () => removeCollaborator(feedId, collaborator.id),
    onError: () => setError("Could not remove them."),
  });

  const labels = catalogue
    .filter((c) => collaborator.capabilities.includes(c.key))
    .map((c) => c.label);

  return (
    <li className="space-y-3 border-b px-4 py-4 last:border-b-0">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <CollaboratorIdentity
            did={collaborator.did}
            profile={profile}
            userId={collaborator.user_id}
            isYou={isYou}
          />
          {collaborator.note && (
            <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
              {collaborator.note}
            </p>
          )}
          <p className="mt-1 text-xs text-gray-500">
            {labels.length > 0 ? labels.join(" · ") : "No capabilities"}
          </p>
          {fromList && (
            /* Editing or removing them here would be undone by the next sync, so the row
               points at the thing that actually decides. */
            <p className="mt-1 text-xs text-gray-500">
              From a bound list — change the list to change this.
            </p>
          )}
        </div>
        <div className="flex shrink-0 gap-2">
          {canManage && !fromList && (
            <Button variant="outline" size="sm" onClick={() => setEditing((v) => !v)}>
              {editing ? "Cancel" : "Change"}
            </Button>
          )}
          {(canManage || isYou) && !fromList && !confirmingRemove && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setConfirmingRemove(true)}
            >
              {isYou ? "Leave feed" : "Remove"}
            </Button>
          )}
        </div>
      </div>

      {/* 🔑 REVOKING USED TO BE ONE CLICK ON A BUTTON SITTING BESIDE "Change", and the two
          looked identical. Asking first is worth the extra click on the one action here
          that takes something away from a person. */}
      {confirmingRemove && (
        <div className="space-y-2 rounded border border-red-300 bg-red-50 p-3 dark:border-red-900 dark:bg-red-950">
          <p className="text-sm">
            {isYou
              ? "Leave this feed? You will lose access to it until its owner adds you again."
              : `Remove ${name} from this feed? They lose access immediately, and will need to be added again to get it back.`}
          </p>
          <div className="flex gap-2">
            <Button size="sm" onClick={() => remove.mutate()} disabled={remove.isPending}>
              {remove.isPending
                ? "Removing…"
                : isYou
                  ? "Yes, leave"
                  : "Yes, remove"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setConfirmingRemove(false)}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {editing && (
        <div className="space-y-3">
          <CapabilityPicker
            catalogue={catalogue}
            selected={caps}
            grantable={grantable}
            onChange={setCaps}
            idPrefix={`c${collaborator.id}`}
          />
          {caps.length === 0 ? (
            /* 🔑 UNTICKING EVERYTHING IS NOT A WAY TO REVOKE, AND USED TO LOOK LIKE ONE.
               Saving an empty set leaves a row that is still a collaborator to
               `/shared-with-me` -- so the feed keeps appearing in their list -- while the
               server's read check is `bool(capabilities)`, so opening it 403s. A person in
               that state has been removed everywhere except where they can see. The intent
               behind emptying the boxes is removal, so offer removal. */
            <div className="space-y-2 rounded border border-amber-300 bg-amber-50 p-3 dark:border-amber-900 dark:bg-amber-950">
              <p className="text-sm">
                With nothing ticked they stay on this feed but cannot open it — a state
                worth nobody's time. Remove them instead?
              </p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={() => remove.mutate()}
                  disabled={remove.isPending}
                >
                  {remove.isPending ? "Removing…" : `Remove ${name}`}
                </Button>
                <Button variant="outline" size="sm" onClick={() => setCaps(collaborator.capabilities)}>
                  Put their capabilities back
                </Button>
              </div>
            </div>
          ) : (
            <Button size="sm" onClick={() => save.mutate()} disabled={save.isPending}>
              {save.isPending ? "Saving…" : "Save changes"}
            </Button>
          )}
        </div>
      )}
      {error && <p className="text-sm text-red-600">{error}</p>}
    </li>
  );
}

function RouteComponent() {
  const { feedId } = Route.useParams();
  const id = parseInt(feedId);
  const { data: feed } = useSuspenseQuery(feedQueryOptions(id));
  const { data: list } = useSuspenseQuery(collaboratorsQueryOptions(id));
  const { data: catalogue } = useSuspenseQuery(capabilityCatalogueQueryOptions());
  const { data: events } = useQuery(collaboratorEventsQueryOptions(id));
  // One hydration call for the whole list rather than one per row.
  const { data: profiles } = useQuery(
    hydratedUsersByDidQueryOptions(
      [
        list.owner_did,
        ...list.collaborators.map((c) => c.did),
        // Also everyone named in the activity log: a removed collaborator is no longer in
        // the list above, but their actions are still the answer to "who did this".
        ...(events?.events ?? []).flatMap((e) => [e.actor_did, e.subject_did]),
      ].filter((d): d is string => !!d),
    ),
  );

  const canManage =
    list.you_are_owner || list.your_capabilities.includes("manage_collaborators");

  // Which row is you. Without this the "(you)" marker never showed and the "Leave feed"
  // button was unreachable -- the server lets anyone remove themselves without permission,
  // but the screen never offered it.
  const { data: me } = useQuery(getCurrentUserQueryOptions());
  const myDid = me?.did ?? me?.user_id;
  // 🔑 THE SERVER REPORTS ADMINS AS OWNERS. `_standing()` short-circuits to owner for
  // anyone in ADMIN_IDS, so an admin gets `you_are_owner: true` on a feed they do not own.
  // Only admins can reach this: a non-owner without the short-circuit gets `false`, and a
  // real owner matches `feed.user_id`.
  const viewingAsAdmin = !!me && list.you_are_owner && me.id !== feed.user_id;
  // ...and whether they ALSO hold a grant here, which is the only case where the admin
  // short-circuit is actually confusing: revoking that grant changes nothing they can see,
  // because their access never came from it. Said unconditionally, the note claimed they
  // were not a collaborator when sometimes they are, and pointed at "removing yourself
  // from the list below" when they are not in the list at all.
  const alsoHoldsGrant =
    !!myDid && list.collaborators.some((c) => c.did === myDid);

  return (
    <Page>
      {/* The same header every other feed sub-page carries, with the same back arrow the
          settings page uses. This screen had its own bare <h1> instead, so it was the one
          feed page with no way back and none of the feed's own controls. */}
      <ViewFeedHeader
        feedId={feedId}
        icon={
          <div className="h-full w-full flex items-center justify-center">
            <ButtonLink
              to="/app/feed/$feedId/view"
              params={{ feedId }}
              variant="ghost"
              size="icon"
            >
              <span className="sr-only">Back to feed</span>
              <ArrowLeftIcon />
            </ButtonLink>
          </div>
        }
      />
      <h2 className="mt-6 mb-4 text-xl font-semibold">Collaborators</h2>

      <div className="space-y-6">
        {viewingAsAdmin && (
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Admin view — this feed belongs to someone else.
            {alsoHoldsGrant &&
              " You can manage it because you are an admin, not because of your grant" +
                " below, so removing that grant will not change what you see here."}
          </p>
        )}
        {!canManage && (
          <p className="rounded border bg-gray-50 px-4 py-3 text-sm dark:bg-gray-900">
            You can see who works on this feed, but not change it. Ask the feed's owner for
            “Manage collaborators” if you need to.
          </p>
        )}

        {canManage && (
          <AddCollaboratorForm
            feedId={id}
            catalogue={catalogue.capabilities}
            grantable={list.you_may_grant}
            presets={catalogue.presets}
          />
        )}

        <div className="rounded-lg border">
          <div className="border-b px-4 py-2 text-sm text-gray-600 dark:text-gray-400">
            {list.collaborators.length === 0
              ? "Its owner is the only person who can work on this feed."
              : `Its owner, and ${list.collaborators.length} ${
                  list.collaborators.length === 1 ? "other person" : "other people"
                }.`}
          </div>

          {/* 🔑 THE OWNER IS LISTED FIRST, AND SAID TO BE THE OWNER. They are not in
              `feed_collaborators`, so this page used to omit them entirely -- which meant a
              feed with one collaborator showed exactly one person, and that person read as
              the owner. Reported as exactly that. */}
          <div className="flex flex-wrap items-start justify-between gap-3 border-b px-4 py-4">
            <div className="min-w-0">
              <CollaboratorIdentity
                did={list.owner_did}
                profile={
                  list.owner_did ? profiles?.[list.owner_did] : undefined
                }
                userId={list.owner_user_id}
                isYou={!!myDid && list.owner_did === myDid}
              />
              <p className="mt-1 text-xs text-gray-500">
                Owner · everything, including publishing and deleting the feed
              </p>
            </div>
          </div>
          {list.collaborators.length > 0 && (
            <ul>
              {list.collaborators.map((c) => (
                <CollaboratorRow
                  key={c.id}
                  feedId={id}
                  collaborator={c}
                  profile={c.did ? profiles?.[c.did] : undefined}
                  catalogue={catalogue.capabilities}
                  grantable={list.you_may_grant}
                  canManage={canManage}
                  isYou={!!myDid && c.did === myDid}
                />
              ))}
            </ul>
          )}
        </div>

        <BoundLists
          feedId={id}
          catalogue={catalogue.capabilities}
          grantable={list.you_may_grant}
          canManage={canManage}
        />

        {events && (
          <CollaboratorActivity
            events={events.events}
            profiles={profiles}
            catalogue={catalogue.capabilities}
          />
        )}
      </div>
    </Page>
  );
}
